import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useLanguage } from "context/LanguageContext";
import dayjs from "dayjs";
import api from "services/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "context/SnackbarContext";

const getFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const parseNumericValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const normalized = String(value).replace(/\s+/g, "").replace(",", ".");
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLineItem = (item, index) => {
  const quantityValue = getFirstDefinedValue(item.quantity, item.qty, item.count, item.amount_qty);
  const unitPriceValue = getFirstDefinedValue(
    item.unit_price,
    item.unitPrice,
    item.price,
    item.rate,
    item.unit_cost
  );
  const totalValue = getFirstDefinedValue(
    item.total_amount,
    item.total,
    item.line_total,
    item.lineTotal,
    item.total_price,
    item.amount,
    item.sum_total
  );

  const quantityNumber = parseNumericValue(quantityValue);
  const unitPriceNumber = parseNumericValue(unitPriceValue);
  const totalNumber = parseNumericValue(totalValue);
  const derivedTotal =
    totalNumber ??
    (quantityNumber !== null && unitPriceNumber !== null ? quantityNumber * unitPriceNumber : null);

  return {
    id: item.id || `temp-${index}`,
    description: getFirstDefinedValue(item.description, item.name, item.title, item.item_name, ""),
    quantity: quantityValue ?? "",
    unit_price: unitPriceValue ?? "",
    total_amount: derivedTotal !== null ? derivedTotal.toFixed(2) : totalValue ?? "",
  };
};

function DocumentDetailsModal({ open, onClose, selectedDocument, companyId }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const [formData, setFormData] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Initialize form data when document changes
  useEffect(() => {
    if (selectedDocument) {
      setFormData({
        supplier_name: selectedDocument.supplier_name || selectedDocument.data?.supplier_name || "",
        supplier_registration: selectedDocument.data?.supplier_registration || "",
        receiver_name: selectedDocument.data?.receiver_name || "",
        receiver_registration: selectedDocument.data?.receiver_registration || "",
        invoice_id: selectedDocument.invoice_id || selectedDocument.data?.invoice_id || "",
        invoice_date:
          selectedDocument.invoice_date || selectedDocument.data?.invoice_date
            ? dayjs(selectedDocument.invoice_date || selectedDocument.data?.invoice_date).format(
                "YYYY-MM-DD"
              )
            : "",
        due_date: selectedDocument.data?.due_date
          ? dayjs(selectedDocument.data?.due_date).format("YYYY-MM-DD")
          : "",
        net_amount: selectedDocument.data?.net_amount ?? "",
        total_tax_amount: selectedDocument.data?.total_tax_amount ?? "",
        total_amount: selectedDocument.total_amount ?? selectedDocument.data?.total_amount ?? "",
        currency: selectedDocument.currency || selectedDocument.data?.currency || "EUR",
      });

      const initialLineItems = Array.isArray(selectedDocument.line_items)
        ? selectedDocument.line_items
        : Array.isArray(selectedDocument.data?.line_items)
        ? selectedDocument.data.line_items
        : [];
      setLineItems(initialLineItems.map((item, index) => normalizeLineItem(item, index)));
    }
  }, [selectedDocument]);

  // Fetch PDF URL when modal opens
  useEffect(() => {
    if (open && selectedDocument?.file_url) {
      setPdfUrl(selectedDocument.file_url);
    } else {
      setPdfUrl(null);
    }
  }, [open, selectedDocument]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;

    // Auto-calculate total if quantity and unit_price are present.
    // If total is edited manually, recalculate unit price from total / quantity.
    if (field === "quantity" || field === "unit_price") {
      const qty = parseNumericValue(newItems[index].quantity);
      const price = parseNumericValue(newItems[index].unit_price);
      if (qty !== null && price !== null) {
        newItems[index].total_amount = (qty * price).toFixed(2);
      }
    }

    if (field === "total_amount") {
      const qty = parseNumericValue(newItems[index].quantity);
      const total = parseNumericValue(newItems[index].total_amount);
      if (qty !== null && qty !== 0 && total !== null) {
        newItems[index].unit_price = (total / qty).toFixed(4);
      }
    }

    setLineItems(newItems);
  };

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      { id: `temp-${Date.now()}`, description: "", quantity: "", unit_price: "", total_amount: "" },
    ]);
  };

  const handleRemoveLineItem = (index) => {
    const newItems = [...lineItems];
    newItems.splice(index, 1);
    setLineItems(newItems);
  };

  const updateDataMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/api/documents/${selectedDocument.id}/update-data/`, data);
      return response.data;
    },
  });

  const updateLineItemsMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(
        `/api/documents/${selectedDocument.id}/update-line-items/`,
        data
      );
      return response.data;
    },
  });

  const handleSave = async () => {
    try {
      const dataPayload = {
        supplier_name: formData.supplier_name,
        supplier_registration: formData.supplier_registration,
        receiver_name: formData.receiver_name,
        receiver_registration: formData.receiver_registration,
        invoice_id: formData.invoice_id,
        invoice_date: formData.invoice_date || null,
        due_date: formData.due_date || null,
        net_amount:
          formData.net_amount && !isNaN(parseFloat(formData.net_amount))
            ? parseFloat(formData.net_amount).toFixed(2)
            : null,
        total_tax_amount:
          formData.total_tax_amount && !isNaN(parseFloat(formData.total_tax_amount))
            ? parseFloat(formData.total_tax_amount).toFixed(2)
            : null,
        total_amount:
          formData.total_amount && !isNaN(parseFloat(formData.total_amount))
            ? parseFloat(formData.total_amount).toFixed(2)
            : null,
        currency: formData.currency,
      };

      const lineItemsPayload = {
        line_items: lineItems.map((item) => {
          const qty =
            item.quantity && !isNaN(parseFloat(item.quantity)) ? parseFloat(item.quantity) : null;
          const price =
            item.unit_price && !isNaN(parseFloat(item.unit_price))
              ? parseFloat(item.unit_price)
              : null;
          const total =
            item.total_amount && !isNaN(parseFloat(item.total_amount))
              ? parseFloat(item.total_amount)
              : null;

          return {
            description: item.description,
            quantity: qty !== null ? qty.toFixed(2) : null,
            unit_price: price !== null ? price.toFixed(4) : null,
            total_amount: total !== null ? total.toFixed(2) : null,
          };
        }),
      };

      await updateDataMutation.mutateAsync(dataPayload);
      await updateLineItemsMutation.mutateAsync(lineItemsPayload);

      queryClient.invalidateQueries(["documents", companyId]);
      showSnackbar(
        t("documentUpdatedSuccessfully") || "Dokumentas sėkmingai atnaujintas",
        "success"
      );
      onClose();
    } catch (error) {
      console.error("Update error:", error);
      showSnackbar(t("failedToUpdateDocument") || "Nepavyko atnaujinti dokumento", "error");
    }
  };

  const isSaving = updateDataMutation.isLoading || updateLineItemsMutation.isLoading;

  const totalQuantity = lineItems.reduce(
    (acc, item) => acc + (parseNumericValue(item.quantity) || 0),
    0
  );
  const totalAmount = lineItems.reduce((acc, item) => {
    const explicitTotal = parseNumericValue(item.total_amount);
    if (explicitTotal !== null) return acc + explicitTotal;

    const quantity = parseNumericValue(item.quantity);
    const unitPrice = parseNumericValue(item.unit_price);
    if (quantity !== null && unitPrice !== null) {
      return acc + quantity * unitPrice;
    }

    return acc;
  }, 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}
      >
        <MDTypography variant="h6">
          {t("documentDetails") || "Dokumento detalės"} - {selectedDocument?.file_name || ""}
        </MDTypography>
        <Box>
          {selectedDocument?.id && (
            <IconButton
              onClick={() =>
                navigate(`/documents/${selectedDocument.id}`, {
                  state: { from: { pathname: "/documents" } },
                })
              }
              title={t("openFullView") || "Atidaryti pilną peržiūrą"}
              sx={{ mr: 1 }}
            >
              <OpenInNewIcon />
            </IconButton>
          )}
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, height: "80vh", display: "flex" }}>
        <Grid container sx={{ height: "100%" }}>
          {/* Left side: PDF Viewer */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              height: "100%",
              borderRight: "1px solid #e0e0e0",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {pdfUrl ? (
              <iframe
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title="Document PDF"
              />
            ) : (
              <MDTypography variant="body2" color="textSecondary">
                {t("pdfNotAvailable") || "PDF peržiūra negalima"}
              </MDTypography>
            )}
          </Grid>

          {/* Right side: Form and Line Items */}
          <Grid item xs={12} md={6} sx={{ height: "100%", overflowY: "auto", p: 3 }}>
            <MDTypography variant="h6" mb={2}>
              {t("documentInformation") && t("documentInformation") !== "documentInformation"
                ? t("documentInformation")
                : "Pagrindinė informacija"}
            </MDTypography>
            <Grid container spacing={2} mb={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("supplierName") || "Tiekėjo pavadinimas"}
                  value={formData.supplier_name}
                  onChange={handleInputChange("supplier_name")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("supplierRegistration") || "Tiekėjo kodas"}
                  value={formData.supplier_registration}
                  onChange={handleInputChange("supplier_registration")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("receiverName") || "Pirkėjo pavadinimas"}
                  value={formData.receiver_name}
                  onChange={handleInputChange("receiver_name")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("receiverRegistration") || "Pirkėjo kodas"}
                  value={formData.receiver_registration}
                  onChange={handleInputChange("receiver_registration")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("invoiceId") || "Sąskaitos numeris"}
                  value={formData.invoice_id}
                  onChange={handleInputChange("invoice_id")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("invoiceDate") || "Sąskaitos data"}
                  type="date"
                  value={formData.invoice_date}
                  onChange={handleInputChange("invoice_date")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("dueDate") || "Apmokėti iki"}
                  type="date"
                  value={formData.due_date}
                  onChange={handleInputChange("due_date")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t("currency") || "Valiuta"}
                  value={formData.currency}
                  onChange={handleInputChange("currency")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("netAmount") || "Suma be PVM"}
                  type="number"
                  value={formData.net_amount}
                  onChange={handleInputChange("net_amount")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("taxAmount") || "PVM suma"}
                  type="number"
                  value={formData.total_tax_amount}
                  onChange={handleInputChange("total_tax_amount")}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t("totalAmount") || "Suma su PVM"}
                  type="number"
                  value={formData.total_amount}
                  onChange={handleInputChange("total_amount")}
                  size="small"
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">{t("lineItems") || "Eilutės"}</MDTypography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleAddLineItem}
                color="primary"
              >
                {t("addLineItem") || "Pridėti eilutę"}
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              {lineItems.length > 0 ? (
                lineItems.map((item, index) => (
                  <Paper
                    key={item.id || index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <MDTypography variant="subtitle2" color="textSecondary" fontWeight="medium">
                        {t("lineItem") && t("lineItem") !== "lineItem" ? t("lineItem") : "Eilutė"} #
                        {index + 1}
                      </MDTypography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveLineItem(index)}
                        sx={{ p: 0.5, mt: -0.5, mr: -0.5 }}
                        title={t("delete") || "Ištrinti"}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t("description") || "Aprašymas"}
                          size="small"
                          multiline
                          maxRows={4}
                          value={item.description}
                          onChange={(e) =>
                            handleLineItemChange(index, "description", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={t("quantity") || "Kiekis"}
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={t("unitPrice") || "Vieneto kaina"}
                          size="small"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) =>
                            handleLineItemChange(index, "unit_price", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={t("total") || "Bendra suma"}
                          size="small"
                          type="number"
                          value={item.total_amount}
                          onChange={(e) =>
                            handleLineItemChange(index, "total_amount", e.target.value)
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ p: 3, textAlign: "center", borderRadius: 2, backgroundColor: "#fdfdfd" }}
                >
                  <MDTypography variant="body2" color="textSecondary">
                    {t("noLineItems") || "Nėra eilučių."}
                  </MDTypography>
                </Paper>
              )}
              {lineItems.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 4,
                  }}
                >
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {t("totalQuantity") || "Bendras kiekis"}:{" "}
                    {Number.isInteger(totalQuantity) ? totalQuantity : totalQuantity.toFixed(2)}
                  </MDTypography>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {t("total") || "Bendra suma"}: {totalAmount.toFixed(2)}
                  </MDTypography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
        <MDButton onClick={onClose} color="secondary">
          {t("cancel") || "Atšaukti"}
        </MDButton>
        <MDButton
          onClick={handleSave}
          color="info"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isSaving}
        >
          {isSaving ? t("saving") || "Saugoma..." : t("save") || "Išsaugoti"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

DocumentDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDocument: PropTypes.object,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DocumentDetailsModal;
